// askSmart-ui/src/pages/TOSP/portal/TenantDashboard.tsx
import React from "react";
import NaturalBreakpointDetail from "../components/NaturalBreakpointDetail";
import { formatCurrency } from "../format";
import {
  formatSpan,
  spanBetween,
  Tenant,
  tenantLabel,
  tenantLeaseExpiry,
  todayISO,
  TospConfiguration,
  Unit,
} from "../types";
import {
  breakpointProgress,
  breakpointTone,
  latestMonth,
  MONTH_NAMES,
  monthsRemaining,
  nextSubmission,
  SALES_DUE_DAY,
  tenantBreakpointDetail,
  YearMonth,
} from "./portalSummary";
import SalesEntryModal from "./SalesEntryModal";
import { salesYearToDate, TenantSalesMonth, yearOnYear } from "./tenantSales";
import { useReveal } from "./useReveal";

// Whole dollars. Sales run to seven figures, where cents are noise — and two
// of them side by side ("$3,487,233 / $4,125,442") only read at a glance
// without them.
const money0 = (value: number): string =>
  `$${Math.round(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

interface TenantDashboardProps {
  tenant: Tenant;
  /** Every business this account holds. One is the ordinary case. */
  tenants: Tenant[];
  onSelectTenant: (tenantId: string) => void;
  /** Other businesses with months still to report, marked in the switcher. */
  owingElsewhere: Set<string>;
  unitById: (unitId: string) => Unit | undefined;
  /** Which Space Types attract percentage rent — the breakpoint depends on it. */
  configuration: Pick<TospConfiguration, "percentageRentSpaceTypes">;
  /** The three detail screens — the dashboard can't navigate to itself. */
  onNavigate: (view: "sales" | "lease" | "expenses") => void;
  sales: TenantSalesMonth[];
  onSubmitSales: (target: YearMonth, grossRevenue: number) => void;
}

interface TileProps {
  label: string;
  value: React.ReactNode;
  /** Whatever the figure needs to be read against, under it. */
  note: React.ReactNode;
  /** A figure that isn't a figure yet, set back so it doesn't read as money. */
  muted?: boolean;
  /** Optional control, on the label row at the right. */
  action?: React.ReactNode;
}

const tile = ({ label, value, note, muted, action }: TileProps) => (
  <div className="tosp-portal-tile">
    <div className="tosp-portal-tile-head">
      <span className="tosp-portal-tile-label">{label}</span>
      {action}
    </div>
    <div className={`tosp-portal-tile-value${muted ? " tosp-portal-tile-value-muted" : ""}`}>
      {value}
    </div>
    <div className="tosp-portal-tile-note">{note}</div>
  </div>
);

/**
 * A tile that goes somewhere, rather than one that states something.
 *
 * The same box as the rest so the six read as one grid, but a button: the
 * whole panel is the target, which is what a thumb expects on a phone.
 */
const navTile = ({
  icon,
  value,
  title,
  sub,
  onClick,
}: {
  /** The pictogram a screen without a figure of its own leads with. */
  icon?: string;
  /** A figure to lead with instead, set like every other tile value. */
  value?: React.ReactNode;
  title: string;
  sub: string;
  onClick: () => void;
}) => (
  <button type="button" className="tosp-portal-tile tosp-portal-tile-nav" onClick={onClick}>
    <span className="tosp-portal-tile-head">
      <span className="tosp-portal-tile-label">{title}</span>
      <span className="tosp-portal-card-chevron" aria-hidden="true">
        ›
      </span>
    </span>
    {value === undefined ? (
      <span className="tosp-portal-tile-nav-icon" aria-hidden="true">
        {icon}
      </span>
    ) : (
      <span className="tosp-portal-tile-value">{value}</span>
    )}
    <span className="tosp-portal-tile-note">{sub}</span>
  </button>
);

// The tenant's landing page: where the year stands, and the way through to the
// two reporting screens. Everything on it is derived — nothing is entered here.
export default function TenantDashboard({
  tenant,
  tenants,
  onSelectTenant,
  owingElsewhere,
  unitById,
  configuration,
  onNavigate,
  sales,
  onSubmitSales,
}: TenantDashboardProps) {
  const now = new Date();
  const year = now.getFullYear();

  const [workingsOpen, setWorkingsOpen] = React.useState(false);
  const [entryOpen, setEntryOpen] = React.useState(false);

  const breakpointDetail = tenantBreakpointDetail(tenant, unitById, configuration, year);
  const breakpoint = breakpointDetail.total;

  // What's actually been reported — the months on the Sales screen, added up.
  // Nothing is invented here: a tenant who has reported nothing sees nothing,
  // and each month they submit moves this figure and the progress bar with it.
  const reported = salesYearToDate(sales, tenant.id, year);
  const salesToDate = reported?.total ?? null;
  // Like for like: this year's reported months against the same months last
  // year. Null until there's a prior year to set them against.
  const comparison = yearOnYear(sales, tenant.id, year);

  const progress = breakpointProgress(salesToDate, breakpoint);
  const remaining = monthsRemaining(now);

  // One clock for everything that moves on arrival: the bar fills, the
  // percentage climbs and both sales figures count up to meet them. The
  // breakpoint itself doesn't move — it's the target being counted towards,
  // and a target that slides is no target at all.
  const reveal = useReveal();

  // What's already in, and what's owing next. The reporting record is a
  // placeholder; the schedule it drives is not.
  // Whatever's furthest on: the standing record, and anything reported since
  // this screen was opened.
  const lastSubmitted: YearMonth | null = latestMonth(
    sales
      .filter((entry) => entry.tenantId === tenant.id)
      .map((entry) => ({ year: entry.year, month: entry.month })),
  );
  const schedule = nextSubmission(now, lastSubmitted, SALES_DUE_DAY);
  const targetMonthName = MONTH_NAMES[schedule.target.month - 1];

  // How long the lease has left, counting up with everything else on arrival.
  //
  // Animated by moving the END DATE towards its real one rather than by
  // scaling the years and days apart — years and days are not proportional to
  // each other, so scaling them separately would show impossible spans like
  // "2 years 400 days" on the way. Shifting a date and re-measuring goes
  // through real spans only, and lands exactly on the true one.
  const expiry = tenantLeaseExpiry(tenant);
  const leaseLeft = React.useMemo(() => {
    if (expiry.state === "unknown") return "—";
    const today = todayISO();
    // An expired lease counts the other way, so its date is behind today.
    const direction = expiry.state === "expired" ? -1 : 1;
    const at = Date.parse(`${today}T00:00:00Z`);
    const moved = new Date(at + direction * Math.round(expiry.days * reveal) * 86_400_000)
      .toISOString()
      .slice(0, 10);
    const span = spanBetween(today, moved);
    if (!span) return "—";
    return direction < 0 ? `+${formatSpan(span)}` : formatSpan(span);
  }, [expiry, reveal]);

  return (
    <div className="tosp-portal">
      <header className="tosp-portal-header">
        <h1 className="tosp-portal-title tosp-portal-title-stacked">
          <span className="tosp-portal-greeting">Welcome to myTOSP for</span>
          {tenants.length > 1 ? (
            // The business name is the switcher rather than a control beside
            // it — it's already the thing the eye lands on. With one business
            // it stays plain text; nothing new appears for the ordinary case.
            <select
              className="tosp-portal-tenant tosp-portal-tenant-select"
              value={tenant.id}
              aria-label="Business"
              onChange={(e) => onSelectTenant(e.target.value)}
            >
              {tenants.map((option) => (
                <option key={option.id} value={option.id}>
                  {tenantLabel(option)}
                  {/* Only the business on screen is prompted for its overdue
                      months, so the others say so here rather than waiting to
                      be found. */}
                  {owingElsewhere.has(option.id) ? " — sales due" : ""}
                </option>
              ))}
            </select>
          ) : (
            <span className="tosp-portal-tenant">{tenantLabel(tenant)}</span>
          )}
        </h1>
      </header>

      <section className="tosp-portal-tiles" aria-label={`${year} summary`}>
        {tile({
          label: "Sales Year to Date",
          value: salesToDate === null ? "—" : money0(salesToDate * reveal),
          note:
            salesToDate === null ? (
              "No sales reported yet"
            ) : !comparison ? (
              `${reported?.monthsReported} month${reported?.monthsReported === 1 ? "" : "s"} reported`
            ) : (
              <>
                <div
                  className={`tosp-portal-trend${
                    (comparison.changePercent ?? 0) < 0 ? " tosp-portal-trend-down" : ""
                  }`}
                >
                  {comparison.changePercent === null
                    ? "No trading last year"
                    : `${comparison.changePercent >= 0 ? "+" : ""}${comparison.changePercent.toFixed(1)}% vs last year`}
                </div>
                {/* The figure the percentage is against — without it, a
                    percentage is a number the tenant can't check. */}
                <div className="tosp-portal-subfigure">
                  Previous YTD {money0(comparison.previous * reveal)}
                </div>
              </>
            ),
          muted: salesToDate === null,
        })}

        {/* The breakpoint itself isn't a tile of its own — it's the figure
            this one is measured against, and it's printed right here. */}
        {tile({
          label: "Natural Breakpoint Progress",
          value: (
            <>
              {progress === null ? "—" : `${(progress * reveal).toFixed(1)}%`}
              {/* The same workings the property manager sees on the lease —
                  one panel, so a query about the breakpoint is answered from
                  the same place on both sides. */}
              {breakpointDetail.lines.length > 0 && (
                <button
                  type="button"
                  className="tosp-info-btn"
                  aria-expanded={workingsOpen}
                  title={
                    workingsOpen ? "Hide the calculation" : "How this is calculated"
                  }
                  onClick={() => setWorkingsOpen((open) => !open)}
                >
                  i
                </button>
              )}
            </>
          ),
          muted: progress === null,
          note:
            progress === null ? (
              breakpoint === null ? (
                "No percentage rent on your lease"
              ) : (
                "Needs sales to compare"
              )
            ) : (
              <>
                <div>
                  {money0((salesToDate as number) * reveal)} / {money0(breakpoint as number)}
                </div>
                {/* Quicker to read than the percentage above it, and the one
                    figure a tenant is likely to check month to month. */}
                <div
                  className="tosp-portal-progress"
                  role="img"
                  aria-label={`${progress.toFixed(1)}% of the natural breakpoint reached`}
                >
                  <div
                    // Toned off the settled figure, not the revealing one, so
                    // the bar does not run red to amber to green on its way in.
                    className={`tosp-portal-progress-fill tosp-portal-progress-${breakpointTone(progress)}`}
                    style={{ width: `${Math.min(100, progress) * reveal}%` }}
                  />
                </div>
              </>
            ),
        })}

        {tile({
          label: "Sales Data",
          // Straight to the screen that holds the reporting form, rather than
          // a second place to submit from.
          action: (
            <button
              type="button"
              className="tosp-portal-tile-action"
              // A month's takings can't be totalled until the month is over,
              // so the button is there but closed until then.
              disabled={!schedule.open}
              title={
                schedule.open
                  ? `Report ${targetMonthName} sales`
                  : `${targetMonthName} isn't over yet — opens ${MONTH_NAMES[schedule.opensOn.getMonth()]} ${schedule.opensOn.getDate()}`
              }
              onClick={() => setEntryOpen(true)}
            >
              Add {targetMonthName} now
            </button>
          ),
          value: lastSubmitted ? (
            <span className="tosp-portal-submitted">
              <span className="tosp-portal-check" aria-hidden="true">
                ✓
              </span>{" "}
              {MONTH_NAMES[lastSubmitted.month - 1]} submitted
            </span>
          ) : (
            <span className="tosp-portal-submitted">Nothing submitted yet</span>
          ),
          note: (
            <>
              <div>
                Next submission: {MONTH_NAMES[schedule.dueOn.getMonth()]}{" "}
                {schedule.dueOn.getDate()}
              </div>
              <div>
                {remaining} month{remaining === 1 ? "" : "s"} remaining in current budget
                cycle
              </div>
            </>
          ),
        })}

        {/* The three detail screens, sized as tiles so the six read as one
            grid rather than a summary with a footer bolted underneath. */}
        {navTile({
          icon: "📈",
          title: "Sales",
          sub: "View current and historical monthly sales data.",
          onClick: () => onNavigate("sales"),
        })}

        {navTile({
          value: leaseLeft,
          title: "Lease Summary",
          sub: "Key dates, the areas you hold and what they may be used for.",
          onClick: () => onNavigate("lease"),
        })}

        {navTile({
          icon: "💰",
          title: "Expenses",
          sub: "Rent, CAM's, Property Taxes, BMVA Fees, Percentage Rent and HST.",
          onClick: () => onNavigate("expenses"),
        })}
      </section>

      {/* A tenant holding one space sees that lease's workings. A tenant
          holding four sees each of them and the total they add up to, since
          the breakpoint on the tile is the sum. */}
      {workingsOpen && (
        <div className="tosp-portal-workings">
          {breakpointDetail.lines.map((line) => (
            <NaturalBreakpointDetail
              key={line.spaceId}
              breakdown={line.breakdown}
              heading={
                breakpointDetail.lines.length === 1
                  ? `${year} Natural Breakpoint`
                  : `${year} Natural Breakpoint — ${line.label}`
              }
              hideTotal={breakpointDetail.lines.length > 1}
            />
          ))}

          {breakpointDetail.lines.length > 1 && (
            <div className="tosp-breakpoint-detail">
              <div className="tosp-breakpoint-total" style={{ marginTop: 0, paddingTop: 0, borderTop: "none" }}>
                <span>Your {year} Natural Breakpoint, all spaces</span>
                <strong>{formatCurrency(breakpoint as number)}</strong>
              </div>
            </div>
          )}
        </div>
      )}

      {entryOpen && (
        <SalesEntryModal
          target={schedule.target}
          dueOn={schedule.dueOn}
          onClose={() => setEntryOpen(false)}
          onSubmit={(grossRevenue) => {
            onSubmitSales(schedule.target, grossRevenue);
            setEntryOpen(false);
          }}
        />
      )}
    </div>
  );
}
